import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ProductCard from "./ProductCard";
import ProductModal from "./ProductModal";
import PackModal from "./PackModal";
import CartModal from "./CartModal";
import GuestEmailModal from "./GuestEmailModal";
import ShopNoticeModal from "./ShopNoticeModal";
import ShopLeaveModal from "./ShopLeaveModal";
import ShopStage from "./ShopStage";
import { fetchAllProducts } from "./productService";
import {
  applyMerchGenerateItemsToProducts,
  generateMerchMockups,
} from "./merchGenerateService";
import { buildImageReadApiUrl, getAiServicesOrigin } from "./mosidaApi";
import {
  enrichPacksWithCatalogProducts,
  isPackProduct,
  mapProductsToPacks,
} from "./packService";
import { fetchMuseumFeeTaxSettings } from "./museumService";
import {
  cartFeeBreakdown,
  startPayOnReceptionCheckout,
  startQrCheckout,
} from "./checkoutHelpers";
import {
  getRemainingStock,
  isProductInStock,
  parseStockQuantity,
  shouldSkipStockCheck,
} from "./stockService";
import {
  buildCartItem,
  buildPackCartItem,
  cartCount,
  cartLineKey,
  findCartLine,
  formatPrice,
  sortShopProducts,
} from "./shopUtils";
import {
  clearShopCheckout,
  readShopArtUrl,
  readShopCart,
  saveShopArtUrl,
  saveShopCart,
  saveShopPayment,
} from "./shopSession";
import "./shop.css";
import "./shopKiosk.css";
import Loader from "../components/Loader";
import shopBurst from "../assets/img/shop-burst.png";
import shopMadeIcon from "../assets/img/shop-made-icon.png";
import LogoBlack from "../assets/img/MOSIDA-black.png";
import {
  getSelectedFavouriteImageUrl,
  getSelectedFavouriteType,
} from "../utils/selectedArt";
import { clearKioskSession } from "../utils/kioskSession";
import { toCdnMediaUrl } from "../utils/cdnMediaUrl";
import { readAuthSession } from "../services/authSession";

/** Raw art URL for merch generate (CDN / S3), not the image-read proxy. */
function resolveRawArtworkUrl() {
  const raw =
    getSelectedFavouriteImageUrl() ||
    readShopArtUrl() ||
    "";
  const trimmed = String(raw || "").trim();
  if (!trimmed) return "";
  return toCdnMediaUrl(trimmed) || trimmed;
}

function resolveArtworkUrl() {
  const raw = resolveRawArtworkUrl();
  if (!raw) return "";
  return buildImageReadApiUrl(raw) || raw;
}

const SHOP_CATEGORIES = [
  { id: "all", label: "ALL PRODUCTS" },
  {
    id: "apparel",
    label: "APPAREL",
    keys: ["apparel", "shirt", "t-shirt", "tshirt", "hoodie", "sweat"],
  },
  {
    id: "accessories",
    label: "ACCESSORIES",
    keys: ["accessor", "cap", "hat", "tote", "bag", "mug"],
  },
  {
    id: "prints",
    label: "PRINTS",
    keys: ["print", "canvas", "poster", "photo", "onsite-printing", "frame"],
  },
  // {
  //   id: "packs",
  //   label: "PACKS",
  //   keys: ["pack"],
  // },
  {
    id: "collectibles",
    label: "COLLECTIBLES",
    keys: ["sticker", "magnet", "collect"],
  },
];

function productCategoryHaystack(product) {
  return [
    product?.name,
    product?.name?.name,
    product?.category,
    product?.category_name,
    product?.categoryName,
    product?.collection,
    product?.product_type,
    product?.sku_code,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function productMatchesCategory(product, category) {
  if (!category || category.id === "all") return true;
  if (category.id === "packs") {
    return isPackProduct(product);
  }
  if (category.id === "prints") {
    const type = String(product?.type || "").toLowerCase();
    if (type === "onsite-printing") return true;
  }
  const haystack = productCategoryHaystack(product);
  return (category.keys || []).some((key) => haystack.includes(key));
}

function CartIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_17919_47542)">
        <path d="M20.732 17.3366C21.732 17.3366 22.612 16.7899 23.0654 15.9633L27.8387 7.30992C28.332 6.42992 27.692 5.33659 26.6787 5.33659H6.94536L5.69203 2.66992H1.33203V5.33659H3.9987L8.7987 15.4566L6.9987 18.7099C6.02536 20.4966 7.30536 22.6699 9.33203 22.6699H25.332V20.0033H9.33203L10.7987 17.3366H20.732ZM8.21203 8.00326H24.412L20.732 14.6699H11.372L8.21203 8.00326ZM9.33203 24.0033C7.86536 24.0033 6.6787 25.2033 6.6787 26.6699C6.6787 28.1366 7.86536 29.3366 9.33203 29.3366C10.7987 29.3366 11.9987 28.1366 11.9987 26.6699C11.9987 25.2033 10.7987 24.0033 9.33203 24.0033ZM22.6654 24.0033C21.1987 24.0033 20.012 25.2033 20.012 26.6699C20.012 28.1366 21.1987 29.3366 22.6654 29.3366C24.132 29.3366 25.332 28.1366 25.332 26.6699C25.332 25.2033 24.132 24.0033 22.6654 24.0033Z" fill="#111" />
      </g>
      <defs>
        <clipPath id="clip0_17919_47542">
          <rect width="32" height="32" fill="white" />
        </clipPath>
      </defs>
    </svg>

  );
}

function ArrowIcon({ direction = "right" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={direction === "right" ? { transform: "rotate(180deg)" } : undefined}
    >
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </svg>
  );
}

const SHIMMER_CARD_COUNT = 6;

function ShopCardShimmer() {
  return (
    <article className="shopCard shopCard--shimmer" aria-hidden="true">
      <div className="shopCard__hit shopCard__hit--shimmer">
        <div className="shopCard__media shopCard__media--shimmer">
          <span className="shopShimmer shopShimmer--media" />
        </div>
        <div className="shopCard__info shopCard__info--shimmer">
          <span className="shopShimmer shopShimmer--line shopShimmer--title" />
          <span className="shopShimmer shopShimmer--line shopShimmer--price" />
          <span className="shopShimmer shopShimmer--line shopShimmer--variants" />
          <span className="shopShimmer shopShimmer--line shopShimmer--stock" />
        </div>
      </div>
    </article>
  );
}

const SORT_OPTIONS = [
  { value: "best", label: "Best Selling" },
  { value: "low", label: "Price: Low to High" },
  { value: "high", label: "Price: High to Low" },
  { value: "a-z", label: "Name (A - Z)" },
  { value: "z-a", label: "Name (Z - A)" },
];

function ShopSortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = SORT_OPTIONS.find((option) => option.value === value) || SORT_OPTIONS[0];

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className={`shopSort${open ? " is-open" : ""}`} ref={rootRef}>
      <span className="shopSort__label">Sort by:</span>
      <button
        type="button"
        className="shopSort__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Sort products"
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selected.label}</span>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="#33363F"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <ul className="shopSort__menu" role="listbox" aria-label="Sort products">
          {SORT_OPTIONS.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={`shopSort__option${option.value === value ? " is-on" : ""}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ShopScreen() {
  const navigate = useNavigate();
  const [artworkUrl, setArtworkUrl] = useState(() => resolveArtworkUrl());

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalProduct, setModalProduct] = useState(null);
  const [paying, setPaying] = useState(false);
  const [cartItems, setCartItems] = useState(() => readShopCart());
  const [cartOpen, setCartOpen] = useState(false);
  const [guestEmailOpen, setGuestEmailOpen] = useState(false);
  const [removedNotice, setRemovedNotice] = useState(0);
  const [addedNotice, setAddedNotice] = useState(0);
  const [museumFees, setMuseumFees] = useState(null);
  useEffect(() => {
    const url = resolveArtworkUrl();
    setArtworkUrl(url);
    if (url) saveShopArtUrl(url);
  }, []);

  useEffect(() => {
    if (artworkUrl) saveShopArtUrl(artworkUrl);
  }, [artworkUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const feeResult = await fetchMuseumFeeTaxSettings();
      if (!cancelled) setMuseumFees(feeResult.data || null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Always fetch catalog + generate fresh (no cache) on product page.
  useEffect(() => {
    let cancelled = false;

    const loadAndGenerate = async () => {
      setLoading(true);
      setError("");

      try {
        const result = await fetchAllProducts();
        if (cancelled) return;
        if (!result.success) {
          setError(result.error || "Failed to load products");
          setProducts([]);
          return;
        }

        const catalog = result.data || [];
        const generateProducts = catalog.filter(
          (product) => product.type === "instore-merchandise"
        );
        const shopMerchandise = catalog.filter(
          (product) =>
            product.type === "instore-merchandise" ||
            product.type === "onsite-printing"
        );
        const packs = enrichPacksWithCatalogProducts(
          mapProductsToPacks(catalog),
          catalog
        );

        if (!shopMerchandise.length && !packs.length) {
          setError("No products available.");
          setProducts([]);
          return;
        }

        const art = resolveRawArtworkUrl();
        if (art) {
          saveShopArtUrl(art);
          const previewUrl = buildImageReadApiUrl(art) || art;
          if (previewUrl !== artworkUrl) setArtworkUrl(previewUrl);
        }

        if (!art) {
          console.warn("[shop] merch generate skipped — no selected artwork");
          setProducts([...shopMerchandise, ...packs]);
          return;
        }

        if (!generateProducts.length) {
          console.warn("[shop] merch generate skipped — no instore-merchandise");
          setProducts([...shopMerchandise, ...packs]);
          return;
        }

        setProducts([...shopMerchandise, ...packs]);

        const origin = getAiServicesOrigin().replace(/\/+$/, "");
        console.info("[shop] merch generate →", {
          url: `${origin}/creative-system/merch/generate`,
          products: generateProducts.length,
          artwork: art.slice(0, 120),
        });

        const artworkType = getSelectedFavouriteType();
        const generated = await generateMerchMockups({
          artwork: art,
          products: generateProducts,
          station: "merch",
          artworkType,
          forceRefresh: true,
        });
        if (cancelled) return;

        if (generated.success && generated.items?.length) {
          const withMockups = applyMerchGenerateItemsToProducts(
            shopMerchandise,
            generated.items
          );
          setProducts([...withMockups, ...packs]);
        } else {
          console.warn(
            "[shop] merch generate failed:",
            generated.error || generated.message || "no items"
          );
          setProducts([...shopMerchandise, ...packs]);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("[shop] merch generate error:", err?.message || err);
        setError(err?.message || "Failed to load products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAndGenerate();
    return () => {
      cancelled = true;
    };
  }, [artworkUrl]);

  const [sortBy, setSortBy] = useState("best");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveIntent, setLeaveIntent] = useState("quit");
  const [categoryId, setCategoryId] = useState("all");
  const activeCategory =
    SHOP_CATEGORIES.find((category) => category.id === categoryId) ||
    SHOP_CATEGORIES[0];

  const openLeaveModal = (intent) => {
    setLeaveIntent(intent);
    setLeaveOpen(true);
  };

  const quitShop = () => {
    clearShopCheckout();
    clearKioskSession();
    setCartItems([]);
    setLeaveOpen(false);
    navigate("/", { replace: true, state: { sessionResetAt: Date.now() } });
  };

  const goGallery = () => {
    setLeaveOpen(false);
    navigate("/my-gallery");
  };

  const visibleProducts = useMemo(
    () =>
      sortShopProducts(
        products.filter((product) => productMatchesCategory(product, activeCategory)),
        sortBy
      ),
    [activeCategory, products, sortBy]
  );
  const pricing = useMemo(
    () => cartFeeBreakdown(cartItems, museumFees),
    [cartItems, museumFees]
  );
  const count = useMemo(() => cartCount(cartItems), [cartItems]);

  const upsertItem = (item) => {
    setCartItems((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const index = next.findIndex(
        (existing) => cartLineKey(existing) === cartLineKey(item)
      );
      if (index >= 0) {
        next[index] = { ...next[index], ...item };
      } else {
        next.push(item);
      }
      saveShopCart(next);
      return next;
    });
  };

  const handleAdd = (product, extras = {}) => {
    const addQty = Math.max(1, Number(extras.quantity) || 1);
    if (isPackProduct(product) || extras.products) {
      const packStock =
        parseStockQuantity(product?.quantity) ??
        parseStockQuantity(product?.stock) ??
        parseStockQuantity(product?.available_quantity);
      if (packStock != null) {
        const inCart = cartItems.reduce((sum, item) => {
          if (String(item?.productId || item?.id || "") !== String(product.id)) {
            return sum;
          }
          if (!(item?.isPack || String(item?.type || "").toLowerCase() === "pack")) {
            return sum;
          }
          return sum + (Number(item.quantity) || 0);
        }, 0);
        const remaining = Math.max(0, packStock - inCart);
        if (remaining <= 0) {
          toast.error("This pack is sold out");
          return;
        }
        if (addQty > remaining) {
          toast.error(`Only ${remaining} in stock`);
          return;
        }
      }
      const incoming = buildPackCartItem(product, {
        quantity: addQty,
        artUrl: artworkUrl,
        selections: extras.selections,
        products: extras.products,
      });
      if (extras.unitPrice != null) incoming.price = extras.unitPrice;
      const existing = findCartLine(cartItems, incoming);
      if (existing) {
        upsertItem({ ...existing, quantity: (existing.quantity || 0) + addQty });
        return;
      }
      upsertItem(incoming);
      return;
    }

    if (!shouldSkipStockCheck(product) && !isProductInStock(product)) {
      toast.error("This item is sold out");
      return;
    }
    if (!shouldSkipStockCheck(product)) {
      const remaining = getRemainingStock(
        product,
        { size: extras.size, color: extras.color },
        cartItems
      );
      if (remaining <= 0) {
        toast.error("This item is sold out");
        return;
      }
      if (addQty > remaining) {
        toast.error(`Only ${remaining} in stock`);
        return;
      }
    }

    const incoming = buildCartItem(product, {
      quantity: addQty,
      size: extras.size,
      color: extras.color,
      artUrl: artworkUrl,
    });
    if (product.merchGeneratedMockup) {
      incoming.merchGeneratedMockup = true;
      incoming.imageUrl = product.picture || product.imgURL || incoming.imageUrl;
      incoming.imgURL = incoming.imageUrl;
    }
    const existing = findCartLine(cartItems, incoming);
    if (existing) {
      upsertItem({ ...existing, quantity: (existing.quantity || 0) + addQty });
      return;
    }
    upsertItem(incoming);
  };

  const handleRemove = (item) => {
    const key = cartLineKey(item);
    setCartItems((prev) => {
      const next = (Array.isArray(prev) ? prev : []).filter(
        (existing) => cartLineKey(existing) !== key
      );
      saveShopCart(next);
      return next;
    });
    setRemovedNotice((tick) => tick + 1);
  };

  const handlePay = async () => {
    if (!cartItems.length || paying) return;
    const items = cartItems;
    const artUrl = artworkUrl || resolveRawArtworkUrl() || "";

    setPaying(true);
    setError("");
    saveShopPayment({});

    try {
      const result = await startQrCheckout(items, {
        artUrl,
        museumFees,
      });
      saveShopPayment({
        qr: result.qrCodeUrl,
        tempDataId: result.tempDataId,
        totalAmount: result.totalAmount,
      });
      setCartOpen(false);
      navigate("/qr-payment");
    } catch (err) {
      const message = err?.message || "Failed to start payment. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setPaying(false);
    }
  };

  const handlePayOnReception = () => {
    if (!cartItems.length || paying) return;
    setCartOpen(false);
    setGuestEmailOpen(true);
  };

  const handleGuestEmailSubmit = async (email) => {
    if (!cartItems.length || paying) return;
    setPaying(true);
    setError("");
    try {
      const result = await startPayOnReceptionCheckout(cartItems, email, {
        artUrl: artworkUrl,
        museumFees,
      });
      saveShopCart([]);
      setCartItems([]);
      setGuestEmailOpen(false);
      navigate("/counter-payment", {
        state: {
          orderNumbers: result.orderNumbers,
          orderNumber: result.orderNumbers[0],
          paymentMethod: "pay_on_reception",
        },
      });
    } catch (err) {
      setError(err?.message || "Failed to place order. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <ShopStage>
      <div className="shopScreen shopScreen--print shopScreen--kiosk">
        {paying && !cartOpen ? <Loader label="Loading QR..." /> : null}
        <header className="galleryPage__header shopKioskHeader">
          <button
            type="button"
            className="galleryPage__back"
            onClick={() => openLeaveModal("back")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            Back
          </button>
          <img className="galleryPage__logo" src={LogoBlack} alt="MOSIDA" />
          <button
            type="button"
            className="galleryPage__quit"
            onClick={() => openLeaveModal("quit")}
          >
            Quit
          </button>
        </header>
        <div className="printShopHero">
          <div className="printShopHero__row">
            <img
              className="printShopHero__burst printShopHero__burst--left"
              src={shopBurst}
              alt=""
              aria-hidden="true"
            />
            <div className="printShopHero__text">
              <h1 className="printShopHero__title">
                TAKE YOUR MASTERPIECE
                <br />
                WITH YOU
              </h1>
              <p className="printShopHero__tag">Your artwork. Your way. Your merch.</p>
            </div>
            <img
              className="printShopHero__burst"
              src={shopBurst}
              alt=""
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="printShopBody">
          <aside className="printShopRail">
            <h2 className="printShopRail__title">PRINT SHOP</h2>
            <hr className="printShopRail__rule" />
            <p className="printShopRail__copy">
              Turn your art into something real. Made to express. Made to share.
            </p>
            <nav className="printShopNav" aria-label="Product categories">
              {SHOP_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`printShopNav__item${category.id === categoryId ? " is-on" : ""}`}
                  onClick={() => setCategoryId(category.id)}
                >
                  {category.label}
                </button>
              ))}
            </nav>
            <div className="printShopMade">
              <img
                className="printShopMade__icon"
                src={shopMadeIcon}
                alt=""
                aria-hidden="true"
              />
              <p>
                <strong>MADE TO ORDER</strong>
                Sustainably produced just for you.
              </p>
            </div>
          </aside>

          <div className="printShopPanel">
            <div className="printShopToolbar">
              <ShopSortDropdown value={sortBy} onChange={setSortBy} />
              <div className="printShopToolbar__actions">
                <button
                  type="button"
                  className="shopIconBtn shopIconBtn--cart"
                  aria-label={`${count} in cart`}
                  onClick={() => setCartOpen(true)}
                >
                  <CartIcon />
                  <span className="shopIconBtn__badge">{count}</span>
                </button>
              </div>
            </div>

            <main className="shopGridWrap">
              {error && !loading ? <p className="shopError">{error}</p> : null}
              <div
                className="shopGrid"
                role={loading ? "status" : undefined}
                aria-live={loading ? "polite" : undefined}
                aria-busy={loading || undefined}
              >
                {loading
                  ? Array.from({ length: SHIMMER_CARD_COUNT }, (_, index) => (
                      <ShopCardShimmer key={`shimmer-${index}`} />
                    ))
                  : visibleProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onSelect={setModalProduct}
                      />
                    ))}
              </div>
            </main>
            <footer className="shopFooter">
              <div className="shopFooter__pay">
                <p className="shopFooter__total">
                  <span>Subtotal</span>
                  <strong>{formatPrice(pricing.subtotal)}</strong>
                </p>
                <span className="shopFooter__divider" aria-hidden="true" />
                <button
                  type="button"
                  className="shopPayBtn"
                  disabled={!cartItems.length || paying}
                  onClick={() => (cartItems.length ? setCartOpen(true) : null)}
                >
                 REVIEW CART <ArrowIcon />
                </button>
              </div>
            </footer>
          </div>
        </div>

        {cartOpen ? (
          <CartModal
            items={cartItems}
            paying={paying}
            museumFees={museumFees}
            onClose={() => setCartOpen(false)}
            onRemove={handleRemove}
            onPay={handlePay}
            onPayOnReception={handlePayOnReception}
          />
        ) : null}
        <GuestEmailModal
          show={guestEmailOpen}
          onHide={() => !paying && setGuestEmailOpen(false)}
          onSubmit={handleGuestEmailSubmit}
          isSubmitting={paying}
          pricing={pricing}
          initialEmail={readAuthSession()?.user?.email || ""}
        />
        <ShopNoticeModal
          key={`removed-${removedNotice}`}
          show={removedNotice > 0}
          variant="cart"
          duration={3000}
          message="ITEM REMOVED SUCCESSFULLY!"
          onHide={() => setRemovedNotice(0)}
        />
        <ShopNoticeModal
          key={`added-${addedNotice}`}
          show={addedNotice > 0}
          variant="cart"
          duration={3000}
          message="ADDED TO CART SUCCESSFULLY!"
          onHide={() => setAddedNotice(0)}
        />
        <ShopLeaveModal
          show={leaveOpen}
          intent={leaveIntent}
          onStay={() => setLeaveOpen(false)}
          onConfirm={() => {
            if (leaveIntent === "back") goGallery();
            else quitShop();
          }}
        />
        {modalProduct && isPackProduct(modalProduct) ? (
          <PackModal
            pack={modalProduct}
            artworkUrl={artworkUrl}
            cartItems={cartItems}
            onClose={() => setModalProduct(null)}
            onConfirm={(extras) => {
              handleAdd(modalProduct, extras);
              setModalProduct(null);
              setAddedNotice((tick) => tick + 1);
            }}
          />
        ) : null}
        {modalProduct && !isPackProduct(modalProduct) ? (
          <ProductModal
            product={modalProduct}
            artworkUrl={
              modalProduct.merchGeneratedMockup ? "" : artworkUrl
            }
            quantity={1}
            cartItems={cartItems}
            onClose={() => setModalProduct(null)}
            onConfirm={(extras) => {
              handleAdd(modalProduct, extras);
              setModalProduct(null);
              setAddedNotice((tick) => tick + 1);
            }}
          />
        ) : null}
      </div>
    </ShopStage>
  );
}

export default ShopScreen;
